import { Image, Spinner, Textarea } from '@chakra-ui/react'
import { mdiCheck, mdiClose } from '@mdi/js'
import Icon from '@mdi/react'
import { useAtom } from 'jotai'
import { useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { Box } from '@/components/ui/box'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Link } from '@/components/ui/link'
import { Text } from '@/components/ui/typography'
import { TEXTAREA_LIMIT } from '@/constants'
import useCustomToast from '@/hooks/toasts'
import useColorModeValue from '@/hooks/useColorModeValue.tsx'
import { queryClient } from '@/QueryClient'
import { getApprovals } from '@/store/tasksApprovals'
import {
    bulkApprovals,
    grpAwardAmountAtom,
    grpAwardReasonAtom,
    grpCategoryAtom,
    isPlusOneApprovalAtom,
    itemsForBulkApprovalsAtom,
    sharepointPermisionAtom,
    showSailPointCertDisclaimerAtom,
} from '@/store/tasksApprovals/detailsDrawer'
import {
    IApproval,
    IApprovalDetail,
    IBulkApprovalsData,
    ICheckboxValue,
    ICompliance,
    ISailPointCertDetails,
    ITaskDetail,
    TIntegratedSystems,
} from '@/types/tasksApprovals'
import { isSailPointCert } from '@/utils/approvals'

const Container = ({
    item,
    onClose,
    itemDetails,
    textAreaMaxLimit = TEXTAREA_LIMIT,
}: {
    item: IApproval | ICompliance
    onClose: () => void
    itemDetails: ITaskDetail | IApprovalDetail | ISailPointCertDetails[] | null
    system?: TIntegratedSystems
    textAreaMaxLimit?: number
}) => {
    const { t } = useTranslation()
    const [isApproveSet, setIsApproveSet] = useState<boolean>(false)
    const [isRejectSet, setIsRejectSet] = useState<boolean>(false)
    const [rejectReason, setRejectReason] = useState<string>('')
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [itemsToApprove] = useAtom(itemsForBulkApprovalsAtom)
    const [amount] = useAtom(grpAwardAmountAtom)
    const [grpCategory] = useAtom(grpCategoryAtom)
    const [grpAwardReason] = useAtom(grpAwardReasonAtom)
    const [addToast] = useCustomToast()
    const [hasDetails, setHasDetails] = useState(false)
    const [sharepointPermision] = useAtom(sharepointPermisionAtom)
    const [isPlusOneApproval] = useAtom(isPlusOneApprovalAtom)
    const [showSailPointCertDisclaimer] = useAtom(showSailPointCertDisclaimerAtom)
    const maxCharsColor = useColorModeValue('gray.500', 'white')
    const sourceSystemURL =
        !Array.isArray(itemDetails) && itemDetails?.action_source_system_parent_url
            ? itemDetails?.action_source_system_parent_url
            : undefined

    // If not ISailPointCertDetails
    const checkBoxes = !Array.isArray(itemDetails)
        ? (itemDetails as IApprovalDetail)?.fields.filter((field) => field.type === 'checkbox')[0]
        : null

    const [checkBoxValues, setCheckBoxValues] = useState<boolean[]>([])

    const handleMandatoryCheckboxClicked = (value: boolean | string, index: number) => {
        // find the index in checkBoxValues and update the state of checkBoxValues
        const newCheckBoxValues = [...checkBoxValues]
        newCheckBoxValues[index] = value as boolean
        setCheckBoxValues(newCheckBoxValues)
    }
    useEffect(() => {
        // Exception for SailPoint Certifications where the details are an empty array
        if (itemDetails && ('fields' in itemDetails || itemDetails.length)) {
            setHasDetails(true)
        }

        // Get values for checkboxes
        if (checkBoxes) {
            setCheckBoxValues((checkBoxes?.value as ICheckboxValue[]).map(() => false))
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [itemDetails])

    if (!item) {
        return
    }

    const onCancel = () => {
        if (isApproveSet || isRejectSet) {
            setIsApproveSet(false)
            setIsRejectSet(false)
        } else {
            onClose()
        }
    }

    const onApprove = () => {
        setIsApproveSet(true)
    }

    const generateDataArray = (name: string, value: string, type: string) => {
        return [
            {
                name,
                value,
                type,
            },
        ]
    }

    const generateDataforBuklApprovals = (action: 'approve' | 'reject') => {
        const bulkApprovalData: IBulkApprovalsData[] = []
        const isApproval = action === 'approve'

        itemsToApprove.map((_item) => {
            const itemData: IBulkApprovalsData = {
                itemId: _item.toString(),
                approve: isApproval,
                rejectionReason: isApproval ? '' : rejectReason,
            }
            // Approve
            if (isApproval) {
                // GRP
                if (item.system_id === 'GRP') {
                    itemData.data = []

                    // isPlusOneApproval
                    itemData.data.push({
                        name: 'isPlusOneApproval',
                        value: isPlusOneApproval.toString(),
                        type: 'string',
                    })

                    if (amount && grpCategory) {
                        itemData.data.push(
                            {
                                name: 'amount',
                                value: amount.toString(),
                                type: 'number',
                            },
                            {
                                name: 'awardCategoryCode',
                                value: grpCategory.toString(),
                                type: 'string',
                            },
                        )
                    }

                    if (grpAwardReason) {
                        itemData.data.push({
                            name: 'reason',
                            value: grpAwardReason,
                            type: 'string',
                        })
                    }
                }
                // SHAREPOINT
                if (item.system_id === 'SHAREPOINT') {
                    itemData.data = generateDataArray('permission', sharepointPermision, 'string')
                }
            }
            // Reject
            else {
                // GRP
                if (item.system_id === 'GRP') {
                    itemData.data = []
                    // isPlusOneApproval
                    itemData.data.push({
                        name: 'isPlusOneApproval',
                        value: isPlusOneApproval.toString(),
                        type: 'string',
                    })
                }
            }

            bulkApprovalData.push(itemData)
        })

        return bulkApprovalData
    }

    const onConfirm = (action: 'approve' | 'reject') => {
        setIsLoading(true)
        void bulkApprovals(item.system_id, generateDataforBuklApprovals(action), 'approvals', false).then((res) => {
            if (res && res.ok) {
                void queryClient.invalidateQueries({ queryKey: ['approvals'] })
                void getApprovals(true)
                action === 'approve'
                    ? addToast('success', t('tasksApprovals.drawer.approvalSuccessful'))
                    : addToast('success', t('tasksApprovals.drawer.rejectionSuccessful'))
            }
            onClose()
            setIsLoading(false)
        })
    }

    const onReject = () => {
        setIsRejectSet(true)
    }

    const getSubmitButton = () => {
        if (isApproveSet && !isLoading) {
            return (
                <Button variant="primary" onClick={() => onConfirm('approve')} textTransform="uppercase" letterSpacing="1.5px">
                    {t('tasksApprovals.drawer.buttons.confirmApproval')}
                </Button>
            )
        } else if (isRejectSet && !isLoading) {
            return (
                <Button
                    variant="primary"
                    onClick={() => onConfirm('reject')}
                    textTransform="uppercase"
                    letterSpacing="1.5px"
                    disabled={!rejectReason}
                >
                    {t('tasksApprovals.drawer.buttons.rejectRequest')}
                </Button>
            )
        } else if (isLoading) {
            return (
                <Button minW="254px" variant="primary" textTransform="uppercase" letterSpacing="1.5px" disabled>
                    <Spinner />
                </Button>
            )
        }

        return null
    }

    return (
        <>
            {isRejectSet && (
                <>
                    <Text fontSize={12}>{t('tasksApprovals.drawer.rejectReason')}</Text>
                    <Textarea
                        mt={2}
                        onChange={(e) => setRejectReason(e.target.value.trimStart())}
                        variant="gsk"
                        style={{ caretColor: 'var(--chakra-colors-orange-400)' }}
                        value={rejectReason}
                        maxH="200px"
                        minH="84px"
                        maxLength={textAreaMaxLimit}
                    />
                    <Text mb="24px" fontSize="12px" pt="4px" color={maxCharsColor}>
                        {t('tasksApprovals.grpDrawer.awardReasonLimit', { limit: textAreaMaxLimit })}
                    </Text>
                </>
            )}

            {!isRejectSet && item.system_id === 'GRP' && !isPlusOneApproval && (
                <Text fontSize="14px" pb="16px" fontWeight="bold">
                    {t('tasksApprovals.grpDrawer.editAwardAmount')}
                </Text>
            )}

            {!isRejectSet && item.system_id === 'SAIL_POINT_CERT' && showSailPointCertDisclaimer && (
                <Box display="flex" alignItems="center" pb="24px">
                    <Image src="/assets/alert_icon.svg" maxW="14px" />
                    <Text pl="8px" fontSize="14px" fontWeight="bold">
                        {t('tasksApprovals.sailPointCertDrawer.footer.selfApproval')}
                    </Text>
                </Box>
            )}

            {!isRejectSet &&
                item.system_id === 'GRP' &&
                checkBoxes &&
                (checkBoxes.value as ICheckboxValue[]).map((field, index) => (
                    <Box pb="24px" key={field.fullText}>
                        <Checkbox
                            checked={checkBoxValues[index]}
                            variant="gsk"
                            onCheckedChange={(e) => handleMandatoryCheckboxClicked(e.checked, index)}
                            gap="12px"
                        >
                            <Text
                                as="span"
                                fontSize="14px"
                                pb="24px"
                                fontWeight="bold"
                                dangerouslySetInnerHTML={{
                                    __html: field.fullText.replace(
                                        field.linkText,
                                        `<a style="text-decoration:underline" target="_blank" href=${field.link}>${field.linkText}</a>`,
                                    ),
                                }}
                            />
                        </Checkbox>
                    </Box>
                ))}

            {!isRejectSet && item.system_id === 'SHAREPOINT' && (
                <Text fontSize="14px" pb="24px" fontWeight="bold">
                    {t('tasksApprovals.sharepointDrawer.permissions')}
                </Text>
            )}

            {!isRejectSet && item.system_id === 'TNE' && (
                <Box display="flex" alignItems="center" pb="24px">
                    <Image src="/assets/alert_icon.svg" maxW="14px" />
                    <Text fontSize="14px" pl="8px" fontWeight="bold">
                        <Trans
                            i18nKey={'tasksApprovals.tneDrawer.footer.delegation'}
                            components={{
                                link1: <Link href={sourceSystemURL} isExternal textDecoration="underline" />,
                            }}
                        />
                    </Text>
                </Box>
            )}

            <Box>
                <Box display="flex" justifyContent="flex-start">
                    <Button variant="secondary" mr="16px" onClick={onCancel}>
                        {isApproveSet || isRejectSet ? t('tasksApprovals.drawer.buttons.cancel') : t('tasksApprovals.drawer.buttons.close')}
                    </Button>
                    {hasDetails &&
                        (getSubmitButton() || (
                            <>
                                <Button
                                    variant="reject"
                                    onClick={onReject}
                                    mr="16px"
                                    fontSize="12px"
                                    letterSpacing="1.5px"
                                    textTransform="uppercase"
                                    fontWeight="bold"
                                    disabled={
                                        (isSailPointCert(item) && itemsToApprove.length === 0) ||
                                        checkBoxValues.some((value) => value === false)
                                    }
                                >
                                    <Icon path={mdiClose} size="16px" style={{ marginRight: '8px' }} />
                                    {t('tasksApprovals.drawer.buttons.reject')}
                                </Button>

                                <Button
                                    variant="primary"
                                    onClick={onApprove}
                                    letterSpacing="1.5px"
                                    textTransform="uppercase"
                                    fontWeight="bold"
                                    disabled={
                                        (isSailPointCert(item) && itemsToApprove.length === 0) ||
                                        checkBoxValues.some((value) => value === false)
                                    }
                                >
                                    <Icon path={mdiCheck} size="16px" style={{ marginRight: '8px' }} />
                                    {t('tasksApprovals.drawer.buttons.approve')}
                                </Button>
                            </>
                        ))}
                </Box>
            </Box>
        </>
    )
}

export default Container
